import { useEffect, useState } from 'react'
import {
  ChevronLeft, ChevronRight, RefreshCw, AlertTriangle,
  BarChart3, Users, CreditCard, ShieldCheck, Tag, Wallet, Bot,
  MessageCircle, Megaphone, Link2, SlidersHorizontal, Activity,
  ScrollText, HardDrive, Trophy, Receipt, Gift,
} from 'lucide-react'
import { useDashboardStore } from '../../store/dashboardStore'
import { getAdminConfig, saveAdminConfig, getAdminStats, getAdminSupport, adminSetViewMode, setImpersonate, getAdminReferrals } from '../../services/api'
import { C, BigStat, Row, Btn, Loading, ErrorLine, Toggle, useAsync, fmtInt, fmtMoney } from './adminUI'

import StatisticsSection from './StatisticsSection'
import UsersSection from './UsersSection'
import FinanceSection from './FinanceSection'
import PaymentsSection from './PaymentsSection'
import PlansSection from './PlansSection'
import AddressesSection from './AddressesSection'
import BotsSection from './BotsSection'
import SupportSection from './SupportSection'
import BroadcastSection from './BroadcastSection'
import UtmSection from './UtmSection'
import SystemSection from './SystemSection'
import HealthSection from './HealthSection'
import AuditSection from './AuditSection'
import BackupsSection from './BackupsSection'
import LeaderboardSection from './LeaderboardSection'
import ReferralSection from './ReferralSection'
import FortuneSection from './FortuneSection'

// Админ-панель = хаб. Сверху — общая статистика (юзеры, оплаты и регистрации
// за сегодня), ниже — разделы кнопками. Тап по кнопке открывает раздел на весь
// экран с кнопкой «назад». Раньше разделов было 14 (включая отдельные
// «Подписки»), теперь «Подписки» встроены в карточку юзера в разделе
// «Пользователи» — там же блокировка, проверка хэша и история оплат.
//
// Тексты панели — русские (её видит только владелец). Разделы «Статистика»,
// «Рассылка» и UTM написаны раньше и переведены на английский — при
// переключении языка приложения они будут английскими, остальные русскими.

const SECTIONS = [
  { key: 'stats',    label: 'Статистика',      icon: BarChart3,        hint: 'выручка, воронка',      Comp: StatisticsSection },
  { key: 'users',    label: 'Пользователи',    icon: Users,            hint: 'карточки, подписки, блоки', Comp: UsersSection },
  { key: 'finance',  label: 'Финансы',         icon: Receipt,          hint: 'журнал оплат, экспорт', Comp: FinanceSection },
  { key: 'referral', label: 'Рефералы',        icon: Gift,             hint: '10% за друзей, выплаты', Comp: ReferralSection, badge: 'refPending' },
  { key: 'fortune',  label: 'Колесо фортуны',  icon: Gift,             hint: 'призы, резерв ⭐, выплаты', Comp: FortuneSection },
  { key: 'payments', label: 'Оплаты',          icon: CreditCard,       hint: 'заявки, подтверждение', Comp: PaymentsSection, badge: 'payPending' },
  { key: 'plans',    label: 'Тарифы',          icon: Tag,              hint: 'цены и сроки',          Comp: PlansSection },
  { key: 'addr',     label: 'Адреса',          icon: Wallet,           hint: 'пул кошельков',         Comp: AddressesSection },
  { key: 'bots',     label: 'Боты и торговля', icon: Bot,              hint: 'мониторинг, PNL',       Comp: BotsSection },
  { key: 'lb',       label: 'Лидерборд',       icon: Trophy,           hint: 'участники, откуда прибыль', Comp: LeaderboardSection },
  { key: 'support',  label: 'Поддержка',       icon: MessageCircle,    hint: 'диалоги с юзерами',     Comp: SupportSection, badge: 'support' },
  { key: 'bcast',    label: 'Рассылка',        icon: Megaphone,        hint: 'сегменты, отложка',     Comp: BroadcastSection },
  { key: 'utm',      label: 'UTM',             icon: Link2,            hint: 'источники трафика',     Comp: UtmSection },
  { key: 'system',   label: 'Система',         icon: SlidersHorizontal, hint: 'биржи, оплаты, флаги', Comp: SystemSection },
  { key: 'health',   label: 'Здоровье',        icon: Activity,         hint: 'сервер, лог, мониторы', Comp: HealthSection },
  { key: 'audit',    label: 'Аудит',           icon: ScrollText,       hint: 'журнал событий',        Comp: AuditSection },
  { key: 'backups',  label: 'Бэкапы',          icon: HardDrive,        hint: 'копии базы',            Comp: BackupsSection },
]

function SectionButton({ item, badge, onClick }) {
  const Icon = item.icon
  return (
    <button
      onClick={onClick}
      style={{
        position: 'relative', display: 'flex', flexDirection: 'column', gap: 6,
        alignItems: 'flex-start', textAlign: 'left',
        padding: '13px 12px', borderRadius: 14,
        background: C.card, border: `1px solid ${C.line}`,
        cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        <Icon size={17} color={C.accent} strokeWidth={2.5} style={{ flexShrink: 0 }} />
        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#fff', flex: 1, minWidth: 0 }}>
          {item.label}
        </div>
        {badge ? (
          <span style={{
            minWidth: 20, textAlign: 'center', padding: '1px 6px', borderRadius: 8,
            background: `${C.warn}2E`, color: C.warn, fontSize: 11, fontWeight: 700,
          }}>{badge}</span>
        ) : (
          <ChevronRight size={14} color={C.dim} style={{ flexShrink: 0 }} />
        )}
      </div>
      <div style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.35 }}>{item.hint}</div>
    </button>
  )
}

export default function AdminPanel() {
  const setAppConfig = useDashboardStore(s => s.setAppConfig)

  const [screen, setScreen] = useState(null)
  const [cfg, setCfg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  // «Режим юзера» (п.1-7 task.txt): состояние берём из localStorage — он же
  // управляет заголовком X-Impersonate на всех запросах (см. services/api.js).
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('scalpex-view-mode') === '1' } catch { return false }
  })

  const stats = useAsync(() => getAdminStats(), [])
  // Только ради счётчика «ждут ответа» на кнопке «Поддержка».
  const support = useAsync(() => getAdminSupport({ status: 'open', limit: 1 }), [])
  // Только ради бейджа «начисления к выплате 5-го» на кнопке «Рефералы».
  const referral = useAsync(() => getAdminReferrals(), [])

  useEffect(() => {
    getAdminConfig()
      .then(c => setCfg(c))
      .catch(e => setError(e?.message || 'Не удалось загрузить конфиг'))
      .finally(() => setLoading(false))
  }, [])

  // При открытии/закрытии раздела — наверх, иначе экран открывается «с середины».
  useEffect(() => { try { window.scrollTo({ top: 0 }) } catch { /* ignore */ } }, [screen])

  // Публичная часть конфига сразу в стор — «Тех. работы» и флаги вступают
  // в силу без перезагрузки приложения.
  const syncStore = (next) => setAppConfig({
    exchanges: next.exchanges,
    payments: next.payments,
    features: next.features,
    maintenance: !!next.maintenance,
    recommendedTradeMode: next.recommendedTradeMode === 'long' ? 'long' : 'short',
  })

  const setGroup = (group, key, val) =>
    setCfg(c => ({ ...c, [group]: { ...c[group], [key]: val } }))

  const handleSave = async () => {
    if (!cfg || saving) return
    setSaving(true); setError(''); setSaved(false)
    try {
      const savedCfg = await saveAdminConfig(cfg)
      setCfg(savedCfg)
      syncStore(savedCfg)
      setSaved(true)
      setTimeout(() => setSaved(false), 2200)
    } catch (e) {
      setError(e?.message || 'Не удалось сохранить')
    } finally {
      setSaving(false)
    }
  }

  // Тумблер «Тех. работы» на главном экране панели сохраняется сразу: это
  // аварийный выключатель, бегать за кнопкой «Сохранить» в такой момент незачем.
  // Бэкенд принимает частичный патч, остальной конфиг не затрагивается.
  const toggleMaintenance = async (v) => {
    if (saving) return
    setSaving(true); setError('')
    try {
      const savedCfg = await saveAdminConfig({ maintenance: v })
      setCfg(savedCfg)
      syncStore(savedCfg)
    } catch (e) {
      setError(e?.message || 'Не удалось сохранить')
    } finally {
      setSaving(false)
    }
  }

  // «Режим юзера» (п.1-7 task.txt): админ смотрит приложение глазами нового
  // юзера. Бэкенд сбрасывает тестового юзера при каждом включении (новый юзер
  // с чистого листа), заголовок X-Impersonate ставится на все запросы кроме
  // админки. После включения перезагружаем страницу — фронт стартует как юзер.
  const toggleViewMode = async (v) => {
    if (saving) return
    setError('')
    try {
      await adminSetViewMode(v)
      setImpersonate(v)
      setViewMode(v)
      // Перезагрузка обязательна: App.jsx при старте читает localStorage и
      // поднимает флаг имперсонации ДО первого запроса /api/users/me.
      window.location.reload()
    } catch (e) {
      setError(e?.message || 'Не удалось переключить режим юзера')
    }
  }

  const open = SECTIONS.find(s => s.key === screen)

  if (open) {
    const Comp = open.Comp
    return (
      <section>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
          paddingBottom: 12, borderBottom: `1px solid ${C.line}`,
        }}>
          <button
            onClick={() => setScreen(null)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34, borderRadius: 11, flexShrink: 0,
              background: C.card, border: `1px solid ${C.line}`,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
          >
            <ChevronLeft size={18} color="#fff" />
          </button>
          <open.icon size={17} color={C.accent} strokeWidth={2.5} />
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', flex: 1 }}>{open.label}</div>
        </div>

        {/* «Система» и «Финансы» хранят часть настроек в admin_config.json
            (в «Финансах» — карточки Telegram Stars, task.txt п.8), поэтому
            обеим секциям отдаём cfg и props сохранения. */}
        {open.key === 'system' || open.key === 'finance'
          ? (
            <Comp
              cfg={cfg}
              setCfg={setCfg}
              setGroup={setGroup}
              onSave={handleSave}
              saving={saving}
              saved={saved}
              error={error}
            />
          )
          : <Comp />}
      </section>
    )
  }

  const s = stats.data?.summary || {}
  const waiting = support.data?.stats?.waitingAnswer || 0
  // Бейдж «Рефералы» — только когда есть что выплачивать: начисления прошлого
  // месяца ещё не отмечены (pendingPrev). Текущий месяц в бейдж не идёт — он
  // станет «к выплате» только 1-го следующего месяца.
  const badges = {
    payPending: s.payPending || 0,
    support: waiting,
    refPending: referral.data?.overview?.pendingPrev > 0
      ? referral.data.overview.prevPendingCount || 1
      : 0,
  }

  return (
    <section>
      {cfg?.maintenance && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12,
          background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)',
          borderRadius: 12, padding: '10px 12px',
        }}>
          <AlertTriangle size={15} color={C.danger} style={{ flexShrink: 0 }} />
          <div style={{ fontSize: 11.5, color: C.danger, lineHeight: 1.45 }}>
            Включён режим «Тех. работы»: юзеры видят заглушку. Вы видите приложение как обычно.
          </div>
        </div>
      )}

      {/* ── Общая статистика ─────────────────────────────────────────────── */}
      <div style={{
        background: C.card, border: `1px solid ${C.line}`, borderRadius: 16,
        padding: 16, marginBottom: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <BarChart3 size={16} color={C.accent} strokeWidth={2.5} />
          <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', flex: 1 }}>Сегодня и всего</div>
          <Btn small ghost icon={RefreshCw} color={C.muted} onClick={() => { stats.reload(); support.reload(); referral.reload() }}>
            Обновить
          </Btn>
        </div>

        {stats.loading && !stats.data ? <Loading /> : (
          <>
            <ErrorLine onRetry={stats.reload}>{stats.error}</ErrorLine>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              <BigStat label="Всего юзеров" value={fmtInt(s.usersTotal)} color="#fff" />
              <BigStat label="Пришло сегодня" value={fmtInt(s.usersToday)} color={C.accent} />
              <BigStat label="Заходили сегодня" value={fmtInt(s.seenToday)} color={C.indigo} />
              <BigStat label="Оплат сегодня" value={fmtInt(s.paymentsToday)} color={C.accent} />
              <BigStat label="Выручка сегодня" value={`$${fmtMoney(s.revenueToday)}`} color={C.warn} />
              <BigStat
                label="Ждут оплаты"
                value={fmtInt(s.payPending)}
                color={s.payPending ? C.warn : C.dim}
              />
            </div>
            <div style={{ height: 10 }} />
            <Row label="Активных подписок" value={fmtInt(s.subActive)} valueColor={C.green} />
            <Row
              label="Диалогов ждут ответа"
              value={fmtInt(waiting)}
              valueColor={waiting ? C.warn : C.dim}
              onClick={() => setScreen('support')}
            />
          </>
        )}
      </div>

      {/* ── Аварийный выключатель ────────────────────────────────────────── */}
      <div style={{
        background: C.card, border: `1px solid ${C.line}`, borderRadius: 16,
        padding: '2px 16px 6px', marginBottom: 14,
      }}>
        {loading ? (
          <Loading text="Конфиг…" />
        ) : (
          <Toggle
            on={!!cfg?.maintenance}
            onChange={toggleMaintenance}
            label="Тех. работы"
            hint="Сохраняется сразу, без кнопки. Остальные настройки — в разделе «Система»."
          />
        )}
      </div>

      {/* ── Режим юзера (п.1-7 task.txt) ─────────────────────────────────── */}
      <div style={{
        background: C.card, border: `1px solid ${C.line}`, borderRadius: 16,
        padding: '2px 16px 6px', marginBottom: 14,
      }}>
        <Toggle
          on={viewMode}
          onChange={toggleViewMode}
          label="🔍 Режим юзера"
          hint="Показывает приложение так, как его видит новый юзер: онбординг, виртуальный бот, плашка оплаты. При каждом включении тестовый юзер сбрасывается. Выход — по плашке «Режим юзера включён» вверху."
        />
      </div>

      <ErrorLine>{error}</ErrorLine>

      {/* ── Разделы кнопками ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {SECTIONS.map(item => (
          <SectionButton
            key={item.key}
            item={item}
            badge={item.badge ? badges[item.badge] : 0}
            onClick={() => setScreen(item.key)}
          />
        ))}
      </div>

      <div style={{ fontSize: 10.5, color: C.dim, marginTop: 14, lineHeight: 1.5, textAlign: 'center' }}>
        Панель видна только админам (ADMIN_IDS). «Сегодня» считается по
        московским суткам (UTC+3) — так же, как во всей статистике бота.
      </div>
    </section>
  )
}
